import Vue from 'vue';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { getSwiper, getSwiperModules } from '../utils';

const ConfiguratorSteps = {
    EDITION: 'edition',
    EXTERIOR: 'exterior',
    INTERIOR: 'interior',
    INTERIOR_MATERIAL: 'interiorMaterial',
    FINANCE: 'finance',
    SUMMARY: 'summary',
    OFFER: 'offer',
};

const totalPrice = {
    cash: 0,
    leasing: 0,
    additionalMethod1: 0,
    additionalMethod2: 0,
    additionalMethod3: 0,
};

gsap.registerPlugin(ScrollTrigger);

Vue.component('configurator-main-component', {
    props: {
        data: {
            type: Object,
        },
        modelSpecs: {
            type: String,
        },
    },

    data() {
        return {
            swiper: null,
            exteriors: null,
            interiors: null,
            interiorMaterials: null,
            hoods: null,
            assetsToShow: null,

            selectedEdition: null,
            selectedExterior: null,
            selectedHood: null,
            selectedInterior: null,
            selectedInteriorMaterial: null,
            hoodIndex: 0,
            selectedFinance: 'cash',

            totalPrice: totalPrice,
            tempPrices: null,
            finalPrice: 0,
            configuratorStep: ConfiguratorSteps.EDITION,
            isMergedInteriorSteps: false,

            finalEditionPrice: 0,
            finalExteriorPrice: 0,
            finalHoodPrice: 0,
            finalInteriorPrice: 0,
            finalInteriorMaterialPrice: 0,

            currentSlide: 0,
            activeAccordion: 'edition',

            // Specs visibility
            specsVisible: false,

            // Chapter functionality
            chapterOpen: false,
            scrollToElement: null,
        };
    },

    computed: {
        toggleChapterClass() {
            return this.chapterOpen ? 'section-chapter--open' : '';
        },

        toggleModelSpecsClass() {
            return this.chapterOpen
                ? 'section-chapter--open model-new-section-chapter--custom-spacing'
                : '';
        },
    },

    async mounted() {
        this.initSelectedItems();
        this.configuratorStep = ConfiguratorSteps.EDITION;

        await this.initSwiper();

        EventBus.$on('close-summary-screen', async () => {
            this.configuratorStep = ConfiguratorSteps.SUMMARY;
            await this.initSwiper();
        });

        if (!JSON.parse(this.modelSpecs)) return;
        this.initSpecsClickHandler();
    },

    created() {
        EventBus.$on('complete-santander-configuration', (data) => {
            this.completeConfigurator();
            this.completeSantanderCalculation(data);
        });

        EventBus.$on('openSpecs', () => {
            this.specsSelectedModelName = this.modelName;
            this.selectedSpecsModelType = '';
            this.selectedSpecsModelSubline = '';
            this.selectedSpecsModelSublineAsset = null;
            document.body.style.overflow = 'hidden';
        });

        EventBus.$on('closeSpecs', () => {
            this.chapterOpen = false;
        });
    },

    destroyed() {},

    watch: {
        selectedEdition: function (value) {
            this.updateDataToShow('edition', value);
        },
        selectedExterior: function (value) {
            this.updateDataToShow('exterior', value);
        },
        selectedHood: function (value) {
            this.updateDataToShow('hood', value);
        },
        selectedInterior: function (value) {
            this.updateDataToShow('interior', value);
            if (this.isMergedInteriorSteps) this.updateTempPrice();
        },
        selectedInteriorMaterial: function () {
            this.updateTempPrice();
        },
        configuratorStep: function (value) {
            this.updateTempPrice();
            switch (value) {
                case ConfiguratorSteps.EDITION:
                case ConfiguratorSteps.EXTERIOR:
                    this.assetsToShow = this.selectedHood.hoodAssets;
                    break;
                case ConfiguratorSteps.INTERIOR:
                case ConfiguratorSteps.INTERIOR_MATERIAL:
                case ConfiguratorSteps.FINANCE:
                    this.assetsToShow = this.selectedInterior.interiorAssets;
                    break;
                default:
                    this.assetsToShow = this.selectedHood.hoodAssets;
                    break;
            }
        },

        assetsToShow: function () {
            setTimeout(() => {
                if (this.swiper) this.swiper.update();
            }, 0.5);
        },
    },

    methods: {
        parseTransformedImage(image) {
            return JSON.parse(image);
        },

        initSelectedItems() {
            const { editions, financingSettings } = this.data;
            const [firstEdition] = editions;
            const [firstExterior] = firstEdition.exteriors;
            const [firstHood] = firstExterior.hoods;
            const [firstInterior] = firstHood.interiors;
            const [firstInteriorMaterial] = firstInterior.interiorMaterials;
            const [firstFinanceOption] = financingSettings.financingOptions;

            this.selectedEdition = firstEdition;
            this.selectedExterior = firstExterior;
            this.selectedHood = firstHood;
            this.selectedInterior = firstInterior;
            this.selectedInteriorMaterial = firstInteriorMaterial;
            this.selectedFinance = firstFinanceOption;
            this.assetsToShow = firstHood.hoodAssets;

            this.isMergedInteriorSteps =
                this.data.generalTranslations.mergeInteriorAndInteriorMaterial;
        },

        normalizePrice(price) {
            return Number(price.toString().replace(/[.,]/g, ''));
        },

        formatPrice(price) {
            return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        },

        sumPricesObject(firstPricesObject, secondPricesObject) {
            const total = {};

            for (const key in firstPricesObject) {
                if (
                    secondPricesObject.hasOwnProperty(key) &&
                    firstPricesObject.hasOwnProperty(key)
                ) {
                    const normalizedFirstPrice = this.normalizePrice(
                        firstPricesObject[key],
                    );
                    const normalizedSecondPrice = this.normalizePrice(
                        secondPricesObject[key],
                    );

                    total[key] = this.formatPrice(
                        normalizedFirstPrice + normalizedSecondPrice,
                    );
                }
            }

            return total;
        },

        updateTempPrice() {
            const editionPrice = this.sumPricesObject(
                this.selectedEdition.prices,
                this.totalPrice,
            );
            const exteriorPrice = this.sumPricesObject(
                this.selectedExterior.prices,
                editionPrice,
            );
            const hoodPrice = this.sumPricesObject(
                this.selectedHood.prices,
                exteriorPrice,
            );
            const interiorPrice = this.sumPricesObject(
                this.selectedInterior.prices,
                hoodPrice,
            );
            const interiorMaterialPrice = this.sumPricesObject(
                this.selectedInteriorMaterial.prices,
                interiorPrice,
            );

            switch (this.configuratorStep) {
                case ConfiguratorSteps.EDITION:
                    this.tempPrices = editionPrice;
                    break;
                case ConfiguratorSteps.EXTERIOR:
                    this.tempPrices = hoodPrice;
                    break;
                case ConfiguratorSteps.INTERIOR:
                    this.tempPrices = interiorPrice;
                    if (this.isMergedInteriorSteps)
                        this.tempPrices = interiorMaterialPrice;
                    break;
                case ConfiguratorSteps.INTERIOR_MATERIAL:
                    this.tempPrices = interiorMaterialPrice;
                    break;
                default:
                    this.tempPrices = interiorMaterialPrice;
                    break;
            }
        },

        updateDataToShow(type, value) {
            switch (type) {
                case 'edition':
                    this.exteriors = value.exteriors;
                    this.updateDataToShow('exterior', value.exteriors[0]);
                    break;
                case 'exterior':
                    this.hoods = value.hoods;
                    this.updateDataToShow('hood', value.hoods[0]);
                    break;
                case 'hood':
                    this.interiors = value.interiors;
                    this.updateDataToShow('interior', value.interiors[0]);
                    break;
                case 'interior':
                    this.interiorMaterials = value.interiorMaterials;
                    break;
                default:
                    console.error('Invalid data type');
                    break;
            }
        },

        checkIfDisabled(item) {
            if (!item.isDisabled) return '';
            return 'configurator-select-type__wrapper-disabled';
        },

        updateSelections(type, value, index) {
            switch (type) {
                case 'edition':
                    this.selectedEdition = value;
                    this.updateSelections('exterior', value.exteriors[0]);
                    break;

                case 'exterior':
                    this.selectedExterior = value;
                    this.updateSelections('hood', value.hoods[this.hoodIndex]);
                    break;

                case 'hood':
                    if (index !== undefined) this.hoodIndex = index;

                    this.selectedHood = value;
                    if (
                        this.configuratorStep === ConfiguratorSteps.EXTERIOR ||
                        this.configuratorStep === ConfiguratorSteps.EDITION
                    ) {
                        this.assetsToShow = value.hoodAssets;
                    }
                    this.updateSelections('interior', value.interiors[0]);
                    break;

                case 'interior':
                    this.selectedInterior = value;

                    if (this.configuratorStep === ConfiguratorSteps.INTERIOR) {
                        this.assetsToShow = value.interiorAssets;
                        if (!this.isMergedInteriorSteps)
                            this.updateSelections(
                                'interiorMaterial',
                                value.interiorMaterials[0],
                            );
                    } else {
                        this.updateSelections(
                            'interiorMaterial',
                            value.interiorMaterials[0],
                        );
                    }
                    break;

                case 'interiorMaterial':
                    this.selectedInteriorMaterial = value;
                    break;

                case 'finance':
                    this.selectedFinance = value;
                    break;

                default:
                    console.error('Invalid selection type');
                    break;
            }
        },

        checkSelected(type, item) {
            switch (type) {
                case 'edition':
                    return this.selectedEdition.uniqueKey === item.uniqueKey;
                case 'exterior':
                    return this.selectedExterior.uniqueKey === item.uniqueKey;
                case 'interior':
                    return this.selectedInterior.uniqueKey === item.uniqueKey;
                case 'hood':
                    return this.selectedHood.uniqueKey === item.uniqueKey;
                case 'interiorMaterial':
                    return (
                        this.selectedInteriorMaterial.uniqueKey ===
                        item.uniqueKey
                    );
                case 'finance':
                    if (
                        this.selectedFinance.financingTitle ==
                        item.financingTitle
                    )
                        return 'configurator-select-finance--active';
                default:
                    return false;
            }
        },

        changeConfiguratorStep() {
            switch (this.configuratorStep) {
                case ConfiguratorSteps.EDITION:
                    this.configuratorStep = ConfiguratorSteps.EXTERIOR;
                    this.activeAccordion = ConfiguratorSteps.EXTERIOR;
                    break;
                case ConfiguratorSteps.EXTERIOR:
                    this.configuratorStep = ConfiguratorSteps.INTERIOR;
                    this.activeAccordion = ConfiguratorSteps.INTERIOR;
                    break;
                case ConfiguratorSteps.INTERIOR:
                    if (this.isMergedInteriorSteps) {
                        this.configuratorStep = ConfiguratorSteps.FINANCE;
                        this.activeAccordion = ConfiguratorSteps.FINANCE;
                    } else {
                        this.configuratorStep =
                            ConfiguratorSteps.INTERIOR_MATERIAL;
                        this.activeAccordion =
                            ConfiguratorSteps.INTERIOR_MATERIAL;
                    }
                    break;
                case ConfiguratorSteps.INTERIOR_MATERIAL:
                    this.configuratorStep = ConfiguratorSteps.FINANCE;
                    this.activeAccordion = ConfiguratorSteps.FINANCE;
                    break;
                case ConfiguratorSteps.FINANCE:
                    this.configuratorStep = ConfiguratorSteps.SUMMARY;
                    this.activeAccordion = ConfiguratorSteps.SUMMARY;
                    this.scrollToTopOfSummary();
                    this.calculateTotalPrice();
                    break;
                case ConfiguratorSteps.SUMMARY:
                    const dataToEmit = this.getDataToEmit();
                    EventBus.$emit('configurator-offer-step', dataToEmit);
                    this.configuratorStep = ConfiguratorSteps.OFFER;
            }

            if (!this.activeAccordion) {
                this.activeAccordion = ConfiguratorSteps.EDITION;
                this.configuratorStep = ConfiguratorSteps.EDITION;
            }
        },

        getDataToEmit() {
            return {
                edition: this.selectedEdition,
                exterior: this.selectedExterior,
                hood: this.selectedHood,
                interior: this.selectedInterior,
                interiorMaterial: this.selectedInteriorMaterial,
                finance: this.selectedFinance,
                totalPrice: this.finalPrice,
                generalTranslations: this.data.generalTranslations,
                offerTranslations: this.data.offerTranslations,
                summaryTranslations: this.data.summaryTranslations,
                editionSettings: this.data.editionSettings,
                exteriorSettings: this.data.exteriorSettings,
                interiorSettings: this.data.interiorSettings,
                interiorMaterialSettings: this.data.interiorMaterialSettings,
                entryId: this.data.entryId,
            };
        },

        goBack() {
            this.configuratorStep = ConfiguratorSteps.FINANCE;
            this.activeAccordion = ConfiguratorSteps.FINANCE;
        },

        changeConfiguratorStepMenu(step) {
            this.configuratorStep = step;
        },

        scrollToTopOfSummary() {
            setTimeout(() => {
                const summary = this.$refs['summaryWrapper'];
                summary.scrollIntoView({ behavior: 'smooth' });
            }, 100);
        },

        calculateTotalPrice() {
            const financeHandle = this.selectedFinance.financingHandle;

            this.finalEditionPrice = this.normalizePrice(
                this.selectedEdition.prices[financeHandle],
            );
            this.finalExteriorPrice = this.normalizePrice(
                this.selectedExterior.prices[financeHandle],
            );
            this.finalHoodPrice = this.normalizePrice(
                this.selectedHood.prices[financeHandle],
            );
            this.finalInteriorPrice = this.normalizePrice(
                this.selectedInterior.prices[financeHandle],
            );
            this.finalInteriorMaterialPrice = this.normalizePrice(
                this.selectedInteriorMaterial.prices[financeHandle],
            );

            this.finalPrice = this.formatPrice(
                this.finalEditionPrice +
                    this.finalExteriorPrice +
                    this.finalHoodPrice +
                    this.finalInteriorPrice +
                    this.finalInteriorMaterialPrice,
            );
        },

        setActiveAccordion(element) {
            const activeAccordionElement =
                this.activeAccordion === element ? null : element;
            this.activeAccordion = activeAccordionElement;
            this.configuratorStep = activeAccordionElement;

            const accordion = this.$refs['configurator-accordion-' + element];
            setTimeout(() => {
                accordion.scrollIntoView({ behavior: 'smooth' });
            }, 100);
        },

        isActiveAccordion(element) {
            return this.activeAccordion === element
                ? 'configurator__line-vertical--active'
                : '';
        },

        async initSwiper() {
            const { Swiper } = await getSwiper();
            const { Pagination } = await getSwiperModules();

            this.swiper = new Swiper('.configurator-main__slider-content', {
                initialSlide: 0,
                slidesPerView: 1,
                modules: [Pagination],
                pagination: {
                    el: '.swiper-pagination',
                    type: 'progressbar',
                    renderProgressbar: function () {
                        return `<span id="progressBar" class="configurator-main__pagination--active"></span>`;
                    },
                },
                slidesPerGroup: 1,
                centeredSlides: true,
                noSwiping: false,
            });

            this.updateProgressBar(0);

            this.swiper.on('slideChange', (swiper) => {
                this.updateProgressBar(swiper.activeIndex);
            });

            this.swiper.on('update', (swiper) => {
                this.swiper.slideTo(0);
                this.updateProgressBar(swiper.activeIndex);
            });
        },

        updateProgressBar(activeIndex) {
            this.currentSlide = activeIndex;
            const progressBar = document.getElementById('progressBar');
            const totalSlides = this.assetsToShow.length;
            const widthPercent = 100 / totalSlides;

            progressBar.style.transform = `translateX(${
                activeIndex * widthPercent
            }%) scaleX(${1 / totalSlides})`;
        },

        getSelectionStepClass(step) {
            if (step == this.configuratorStep) {
                return 'configurator-main__selection-step--active';
            }
        },

        getActiveAccordionClass(step) {
            if (step != this.configuratorStep) return;
            return 'configurator-accordion__icon-text--active';
        },

        getTotalPrice(option, currency) {
            const price = this.tempPrices[option.financingHandle];
            if (currency.includes('{price}')) {
                return currency.replace('{price}', price);
            }
            return `${currency} ${price}`;
        },

        getCalculatedPriceWithCopy(currency, data) {
            let price = data;
            if (data == null)
                price = this.tempPrices[this.selectedFinance.financingHandle];
            if (currency.includes('{price}')) {
                return currency.replace('{price}', price);
            }
            return `${currency} ${price}`;
        },

        getPriceToRender() {
            return this.tempPrices[this.selectedFinance.financingHandle];
        },

        getTotalPriceWithCopy() {
            const priceCopy = this.selectedFinance.priceText;
            const price = this.tempPrices[this.selectedFinance.financingHandle];
            if (!priceCopy) return null;
            return priceCopy.replace('{price}', price);
        },

        getSelectionPrice(type) {
            return type.prices[this.selectedFinance.financingHandle];
        },

        checkIfDisabledExterior(item) {
            const matchHood = item.hoods.find(
                (hood) => hood.uniqueKey === this.selectedHood.uniqueKey,
            );

            if (!matchHood || !matchHood.isDisabled) return '';
            return 'configurator-select-type__wrapper-disabled';
        },

        getPaymentDisclaimer(option) {
            return this.selectedEdition.paymentDisclaimers[
                option.financingHandle
            ];
        },

        initSpecsClickHandler() {
            setTimeout(() => {
                document
                    .querySelector('a[href="#specification"]')
                    .addEventListener('click', this.toggleSeeFullSpecs);
            }, 1000);
        },

        toggleSeeFullSpecs(e) {
            e.preventDefault();
            this.chapterOpen = !this.chapterOpen;
            EventBus.$emit('openSpecs');
            // reset scrolltrigger when section opens/closes
            ScrollTrigger.refresh();
            if (this.chapterOpen) {
                gsap.fromTo(
                    this.$el.querySelector('.section-chapter'),
                    { height: 0 },
                    { height: 'auto', duration: 0.6 },
                );
                this.scrollToElement = e.currentTarget;
                zenscroll.to(this.$el.querySelector('.section-chapter'));
                // datalayer
                if (this.$root.handle == 'mgMotorsFrance') {
                    dataLayer.push({
                        event: 'contentClic',
                        eventCategory: 'Showroom Content',
                        eventAction: e.currentTarget.dataset.section,
                        eventLabel: e.currentTarget.dataset.model,
                    });
                }
            } else {
                gsap.fromTo(
                    this.$el.querySelector('.section-chapter'),
                    { height: 'auto' },
                    { height: 0, duration: 0.6, delay: 0.4 },
                );
                zenscroll.to(this.$el, 700);
                if (checkCCM19Consent() || checkOneTrustConsent()) {
                    // check if you are on production-enviroment
                    if (this.$root.handle == 'mgMotorsFrance') {
                        gtag('event', 'conversion', {
                            allow_custom_scripts: true,
                            send_to: 'DC-10256326/mg-autos/fr_in002+unique',
                        }); //french
                    }
                    if (this.$root.handle == 'mgMotorsNorway') {
                        gtag('event', 'conversion', {
                            allow_custom_scripts: true,
                            send_to: 'DC-10210913/retar0/no_re001+standard',
                        }); //norway
                    }
                }
            }
        },
    },
});
